import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { conditionalSocket } from "../../types";

/**
 * Confidence Threshold Step
 *
 * A control flow step that evaluates a confidence score against a threshold
 * to enable conditional branching in workflows.
 *
 * Use cases:
 * - Invoice matching: branch based on match confidence
 * - AI predictions: require human review for low-confidence results
 * - Quality checks: route items based on quality scores
 *
 * Example workflow:
 * ```
 * Match Invoice → Confidence Threshold
 *                    ├─ [thresholdMet] → Auto-approve
 *                    └─ [thresholdNotMet] → Human review
 * ```
 */
export const confidenceThresholdStep = defineStepConfig({
  type: "confidence_threshold",
  name: "Confidence Threshold",
  description: "Evaluate confidence score against threshold for branching",
  category: "Control Flow",

  inputSchema: z.object({
    confidence: z
      .number()
      .min(0)
      .max(1)
      .describe("Confidence score to evaluate (0-1)"),
  }),

  outputSchema: z.object({
    thresholdMet: conditionalSocket(z.number().min(0).max(1))
      .optional()
      .describe("Confidence value when threshold is met (output socket)"),
    thresholdNotMet: conditionalSocket(z.number().min(0).max(1))
      .optional()
      .describe("Confidence value when threshold is not met (output socket)"),
  }),

  configSchema: z.object({
    threshold: z
      .number()
      .min(0)
      .max(1)
      .prefault(0.95)
      .describe("Confidence threshold (default: 0.95)"),
  }),

  emits: [] as const,
});
